<script lang="ts">
  import { mdiEyeOutline as standardDetailsIcon } from "@mdi/js"

  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import { formatPropertyUnitDisplay } from "../../../../../../../../../_shared/_logic/unit-labels"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import type { PopoverMenuItem } from "@kontextwerk/web-sdk/ui/overlay/popover/types"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import TableFormCell from "@kontextwerk/web-sdk/ui/layout/TableFormCell.svelte"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    mdiArchiveOutline,
    mdiArchiveArrowUpOutline,
    mdiDelete,
    mdiOpenInNew,
    mdiPencil,
    mdiPlus,
  } from "@mdi/js"
  import {
    formatPropertyCustomerRole,
    getPropertyCustomerRoleStatus,
    normalizePropertyCustomerRole,
    outreachEnabledToSelectValue,
    propertyCustomerOutreachOptions,
    propertyCustomerRoleOptions,
    selectValueToOutreachEnabled,
    type PropertyCustomerOutreachValue,
  } from "../../../../../../../_shared/_logic/customer-roles"
  import {
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../../../../_shared/_logic/customer-registry-action-tooltips"
  import {
    formatPropertyUnitType,
    isBuildingPropertyUnit,
    type PropertyUnitType,
  } from "../../../../../../../_shared/_logic/units"
  import { archivePresentation } from "../../../../../../../_shared/_logic/archive-presentation"

  import type { PropertyUnitRow } from "./_logic/types"

  type UnitSection = {
    id: string
    unit: PropertyUnitRow | null
    label: string
    number: string
    notes: string
    links: PropertyCustomer[]
    unassigned: boolean
  }

  type UnitContactTableRow = DataRow & {
    link: PropertyCustomer
    customer: Customer | undefined
    customerName: string
    roleValue: PropertyCustomerRole
    roleLabel: string
    outreachValue: PropertyCustomerOutreachValue
    outreachLabel: string
  }

  type Props = {
    units: PropertyUnitRow[]
    links?: PropertyCustomer[]
    customers?: Customer[]
    showCustomers?: boolean
    showActions?: boolean
    onEdit?: (unit: PropertyUnitRow) => void
    onArchive?: ((unit: PropertyUnitRow) => void) | null
    onRestore?: ((unit: PropertyUnitRow) => void) | null
    onAssignContacts?: ((unit: PropertyUnitRow) => void) | null
    onUpdateLinkRole?:
      | ((link: PropertyCustomer, role: PropertyCustomerRole) => void | Promise<void>)
      | null
    onUpdateLinkOutreach?:
      | ((link: PropertyCustomer, outreachEnabled: boolean) => void | Promise<void>)
      | null
    onRemoveLink?: ((link: PropertyCustomer) => void | Promise<void>) | null
    onOpenCustomer?: ((customer: Customer) => void) | null
    disabled?: boolean
    emptyLabel?: string
    setRefreshFunction?: (fn: () => void) => void
    theme?: "light" | "dark"
  }

  const baseColumns: ColumnDef[] = [
    {
      id: "unit_label",
      label: "Einheit/Name",
      isPrimary: true,
      sortable: true,
      sortKey: "unit_label",
      sortType: "text",
    },
    {
      id: "unit_number",
      label: "Einheitsnummer",
      sortable: true,
      sortKey: "unit_number",
      sortType: "text",
    },
    {
      id: "unitTypeLabel",
      label: "Typ",
      sortable: true,
      sortKey: "unitTypeLabel",
      sortType: "text",
    },
    { id: "notes", label: "Notiz" },
  ]

  const contactColumns: ColumnDef[] = [
    {
      id: "customerName",
      label: "Ansprechpartner",
      isPrimary: true,
      sortable: true,
      sortKey: "customerName",
      sortType: "text",
    },
    { id: "roleLabel", label: "Rolle", sortable: true, sortKey: "roleLabel", sortType: "text" },
    {
      id: "outreachLabel",
      label: "Darf angesprochen werden",
      sortable: true,
      sortKey: "outreachLabel",
      sortType: "text",
    },
  ]

  let {
    units = [],
    links = [],
    customers = [],
    showCustomers = false,
    showActions = true,
    onEdit = () => {},
    onArchive = null,
    onRestore = null,
    onAssignContacts = null,
    onUpdateLinkRole = null,
    onUpdateLinkOutreach = null,
    onRemoveLink = null,
    onOpenCustomer = null,
    disabled = false,
    setRefreshFunction = () => {},
    emptyLabel = "Noch keine Einheiten hinterlegt.",
    theme = "light",
  }: Props = $props()

  const customerById = $derived(
    Object.fromEntries(customers.map((customer) => [customer.id, customer])),
  )

  const columns: ColumnDef[] = $derived([
    ...baseColumns,
    ...(showCustomers
      ? [
          {
            id: "customers",
            label: "Ansprechpartner",
            sortable: true,
            sortKey: "customers",
            sortType: "text",
          } satisfies ColumnDef,
        ]
      : []),
  ])

  const formatCustomers = (unitId?: string | null) => {
    if (!showCustomers || !links?.length) return "–"

    const names = links
      .filter((link) => link.property_unit_id === unitId)
      .map((link) => customerById[link.customer_id]?.display_name)
      .filter(Boolean)

    return names.length ? names.join(", ") : "Keine Zuordnung"
  }

  const rows: Array<DataRow & { unit: PropertyUnitRow }> = $derived(
    (units ?? []).map((unit, idx) => {
      const key = unit.id ?? unit.client_id ?? `unit-${idx}`

      return {
        id: key,
        unit_label: formatPropertyUnitDisplay(unit),
        unit_number: unit.unit_number ?? "–",
        unitTypeLabel: formatPropertyUnitType(unit.unit_type),
        notes: unit.notes || "–",
        customers: formatCustomers(unit.id),
        unit,
      }
    }),
  )

  const unitSections = $derived.by<UnitSection[]>(() => {
    const sections = (units ?? []).map((unit, idx) => {
      const key = unit.id ?? unit.client_id ?? `unit-${idx}`

      return {
        id: key,
        unit,
        label: formatPropertyUnitDisplay(unit),
        number:
          [formatPropertyUnitType(unit.unit_type), unit.unit_number || null]
            .filter(Boolean)
            .join(" · ") || "–",
        notes: unit.notes || "–",
        links: unit.id ? (links ?? []).filter((link) => link.property_unit_id === unit.id) : [],
        unassigned: false,
      }
    })
    const unassignedLinks = (links ?? []).filter((link) => !link.property_unit_id)

    if (showCustomers && unassignedLinks.length) {
      sections.push({
        id: "__unassigned",
        unit: null,
        label: "Liegenschaft allgemein / Ohne Einheit",
        number: "–",
        notes: "Bestehende Zuordnungen ohne konkrete Einheit.",
        links: unassignedLinks,
        unassigned: true,
      })
    }

    return sections
  })

  const refreshKey = $derived(
    showCustomers
      ? unitSections
          .map(
            (section) =>
              `${section.id}:${section.links.map((link) => `${link.id}:${link.role}:${link.outreach_enabled}`).join(",")}`,
          )
          .join("|")
      : rows.map((row) => row.id).join("|"),
  )

  const getRows: TableGetRows = (params) =>
    getSortedTablePage({
      rows,
      columns,
      sort: (params as { sort?: unknown }).sort as any,
      limit: params.limit,
      offset: params.offset,
    })

  const contactRowsForSection = (section: UnitSection): UnitContactTableRow[] =>
    section.links
      .map((link) => {
        const customer = customerById[link.customer_id]
        const roleValue = normalizePropertyCustomerRole(link.role)

        return {
          id: link.id,
          link,
          customer,
          customerName: customer?.display_name ?? "Unbekannter Ansprechpartner",
          roleValue,
          roleLabel: formatPropertyCustomerRole(roleValue),
          outreachValue: outreachEnabledToSelectValue(link.outreach_enabled),
          outreachLabel: link.outreach_enabled === false ? "Nein" : "Ja",
        }
      })
      .sort((left, right) => left.customerName.localeCompare(right.customerName, "de"))

  const getContactRows =
    (section: UnitSection): TableGetRows =>
    (params) =>
      getSortedTablePage({
        rows: contactRowsForSection(section),
        columns: contactColumns,
        sort: (params as { sort?: unknown }).sort as any,
        limit: params.limit,
        offset: params.offset,
      })

  const updateRole = async (row: UnitContactTableRow, section: UnitSection) => {
    const nextRole = normalizePropertyCustomerRole(row.roleValue)

    if (
      nextRole === row.link.role ||
      disabled ||
      section.unit?.effective_archived_at ||
      !onUpdateLinkRole
    )
      return

    await onUpdateLinkRole(row.link, nextRole)
  }

  const updateOutreach = async (row: UnitContactTableRow, section: UnitSection) => {
    const nextValue = selectValueToOutreachEnabled(row.outreachValue)

    if (
      nextValue === row.link.outreach_enabled ||
      disabled ||
      section.unit?.effective_archived_at ||
      !onUpdateLinkOutreach
    )
      return

    await onUpdateLinkOutreach(row.link, nextValue)
  }

  const roleTooltipForSection = (section: UnitSection) => {
    const parts = [section.label, section.number && section.number !== "–" ? section.number : null]
      .filter(Boolean)
      .join(" - ")

    return `Diese Rolle gilt für ${parts || "diese Einheit"}.`
  }

  const archiveTooltip = (unit: PropertyUnitRow) => {
    if (!unit.effective_archived_at) return ""

    const presentation = archivePresentation(
      unit.effective_archived_at,
      unit.effective_archive_reason,
    )

    return unit.archive_source === "PARENT"
      ? `Über die archivierte Liegenschaft ausgeblendet. ${presentation}`
      : presentation
  }
  const unitOptions = (unit: PropertyUnitRow): PopoverMenuItem[] => [
    ...(onArchive && !unit.effective_archived_at && !isBuildingPropertyUnit(unit)
      ? [
          {
            id: "archive",
            label: "Archivieren",
            icon: mdiArchiveOutline,
            reason: customerRegistryActionTooltips.unitArchive,
            tooltipClassName: CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
            onClick: () => onArchive?.(unit),
          },
        ]
      : []),
    ...(onRestore && unit.archive_source === "SELF"
      ? [
          {
            id: "restore",
            label: "Wiederherstellen",
            icon: mdiArchiveArrowUpOutline,
            reason: customerRegistryActionTooltips.unitRestore,
            tooltipClassName: CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
            onClick: () => onRestore?.(unit),
          },
        ]
      : []),
  ]
</script>

{#if showCustomers}
  {#if !unitSections.length}
    <div class="empty-state">{emptyLabel}</div>
  {:else}
    <div class="unit-sections">
      {#each unitSections as section (section.id)}
        <section class="unit-section">
          <SectionTitle
            title={section.label}
            subtitle={section.number !== "–" ? section.number : ""}
            description={section.notes && section.notes !== "–" ? section.notes : ""}
            small={true}
            darkMode={theme === "dark"}
            brightBackground={theme === "light"}
            styleOuter="margin-bottom: 0.8rem;"
          >
            {#snippet buttonSnippet()}
              {#if showActions && !section.unassigned && section.unit}
                {#if onAssignContacts && !section.unit.effective_archived_at}
                  <CTA
                    type="secondaryButton"
                    darkMode={theme == "dark"}
                    content="Ansprechpartner"
                    tooltipContent={customerRegistryActionTooltips.assignContactsToUnit}
                    tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
                    onClick={() => onAssignContacts?.(section.unit as PropertyUnitRow)}
                    {disabled}
                  >
                    {#snippet iconSnippet()}
                      <Icon path={mdiPlus} />
                    {/snippet}
                  </CTA>
                {/if}
                <CTA
                  type="secondaryButton"
                  darkMode={theme == "dark"}
                  content="Bearbeiten"
                  tooltipContent="Einheit bearbeiten"
                  onClick={() => onEdit(section.unit as PropertyUnitRow)}
                  disabled={disabled || Boolean(section.unit.effective_archived_at)}
                  options={{
                    label: "Einheitsoptionen",
                    disabled,
                    items: unitOptions(section.unit),
                  }}
                >
                  {#snippet iconSnippet()}
                    <Icon path={mdiPencil} />
                  {/snippet}
                </CTA>
              {/if}
            {/snippet}
          </SectionTitle>

          {#if section.unit?.effective_archived_at}
            <div class="archive-badge">
              <StatusTag
                variant="neutral"
                size="sm"
                label={section.unit.archive_source === "PARENT"
                  ? "Liegenschaft archiviert"
                  : "Archiviert"}
                icon={mdiArchiveOutline}
                color="#7b8794"
                tooltipContent={archiveTooltip(section.unit)}
              />
            </div>
          {/if}

          <Table
            rowActions={{
              resolve: (row, rowIndex) =>
                buildTableRowActionSet(
                  ((row) => {
                    const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] =
                      []

                    const contactRow = row as UnitContactTableRow

                    if (contactRow.customer && onOpenCustomer) {
                      actions.push({
                        id: "action-0",
                        label: "Ansprechpartner öffnen",
                        onClick: () => onOpenCustomer?.(contactRow.customer as Customer),
                        icon: standardDetailsIcon,
                        tooltip: "Ansprechpartner öffnen",
                      })
                    }

                    if (showActions && onRemoveLink && !section.unit?.effective_archived_at) {
                      actions.push({
                        id: "action-1",
                        label: "Zuordnung entfernen",
                        onClick: () => onRemoveLink?.(contactRow.link),
                        icon: mdiDelete,
                        disabled: disabled,
                        tooltip: customerRegistryActionTooltips.removeUnitContactAssignment,
                        tone: "danger",
                      })
                    }

                    return actions
                  })(row) ?? [],
                  { primaryId: "action-0", inlineIds: ["action-2"] },
                ),
            }}
            columns={contactColumns}
            getRows={getContactRows(section)}
            refreshKey={`${refreshKey}:${section.id}`}
            {theme}
            urlStateId={`kehrblatt-property-unit-contacts-${section.id}`}
            mode="table"
            showActions={true}
            actionsHeaderLabel="Aktionen"
          >
            {#snippet tableCell({ row, column })}
              {@const contactRow = row as UnitContactTableRow}
              {#if column.id === "roleLabel"}
                {#if showActions && onUpdateLinkRole && !section.unit?.effective_archived_at}
                  <TableFormCell style="width: min(100%, 18rem);">
                    <Input
                      type="select"
                      placeholder="Rolle"
                      labelVisibility="hidden"
                      options={propertyCustomerRoleOptions}
                      bind:value={contactRow.roleValue}
                      brightBackground={theme === "light"}
                      {disabled}
                      onChange={() => updateRole(contactRow, section)}
                    />
                  </TableFormCell>
                {:else}
                  <StatusTag
                    variant="neutral"
                    size="sm"
                    status={getPropertyCustomerRoleStatus(contactRow.roleValue)}
                    tooltipContent={roleTooltipForSection(section)}
                  />
                {/if}
              {:else if column.id === "outreachLabel"}
                {#if showActions && onUpdateLinkOutreach && !section.unit?.effective_archived_at}
                  <TableFormCell style="width: min(100%, 18rem);">
                    <Input
                      type="select"
                      placeholder="Darf angesprochen werden"
                      labelVisibility="hidden"
                      options={propertyCustomerOutreachOptions}
                      bind:value={contactRow.outreachValue}
                      brightBackground={theme === "light"}
                      {disabled}
                      onChange={() => updateOutreach(contactRow, section)}
                    />
                  </TableFormCell>
                {:else}
                  {contactRow.outreachLabel}
                {/if}
              {:else}
                {(row as any)[column.id] ?? ""}
              {/if}
            {/snippet}

            {#snippet empty()}
              <div class="empty-state">Noch keine Ansprechpartner für diese Einheit zugeordnet.</div
              >
            {/snippet}
          </Table>
        </section>
      {/each}
    </div>
  {/if}
{:else}
  <Table
    rowActions={{
      resolve: (row, rowIndex) =>
        buildTableRowActionSet(
          ((row) => {
            const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

            if (showActions) {
              const unitRow = row as DataRow & { unit: PropertyUnitRow }

              actions.push({
                id: "action-0",
                label: "Einheit bearbeiten",
                onClick: () => onEdit(unitRow.unit),
                icon: mdiPencil,
                disabled: disabled || Boolean(unitRow.unit.effective_archived_at),
                tooltip: "Einheit bearbeiten",
              })

              if (
                onArchive &&
                !unitRow.unit.effective_archived_at &&
                !isBuildingPropertyUnit(unitRow.unit)
              ) {
                actions.push({
                  id: "action-1",
                  label: "Wohneinheit archivieren",
                  onClick: () => onArchive(unitRow.unit),
                  icon: mdiArchiveOutline,
                  disabled: disabled,
                  tooltip: customerRegistryActionTooltips.unitArchive,
                })
              }

              if (onRestore && unitRow.unit.archive_source === "SELF") {
                actions.push({
                  id: "action-2",
                  label: "Wohneinheit wiederherstellen",
                  onClick: () => onRestore(unitRow.unit),
                  icon: mdiArchiveArrowUpOutline,
                  disabled: disabled,
                  tooltip: customerRegistryActionTooltips.unitRestore,
                })
              }
            }

            return actions
          })(row) ?? [],
          { primaryId: "action-0", inlineIds: ["action-2"] },
        ),
    }}
    {columns}
    {getRows}
    {refreshKey}
    {theme}
    {setRefreshFunction}
    urlStateId="kehrblatt-property-units"
    mode="table"
    {showActions}
    actionsHeaderLabel="Aktionen"
  >
    {#snippet empty()}
      <div class="empty-state">{emptyLabel}</div>
    {/snippet}
  </Table>
{/if}

<style lang="less">
  .unit-sections {
    display: grid;
    gap: 1rem;
  }

  .empty-state {
    padding: 0.4rem 0;
    color: var(--text-invers-100);
  }

  .archive-badge {
    margin: -0.35rem 0 0.8rem;
  }
</style>
